import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * One button a toast may carry.
 *
 * Exists for undo. A reversible bulk action — archiving forty cards — is best
 * announced with the way back attached to the announcement, rather than by
 * asking first: a confirm dialog on something reversible only teaches people to
 * dismiss dialogs, while an undo sits exactly where somebody looks after
 * realising they did the wrong thing.
 */
export interface ToastAction {
  label: string;
  run: () => void | Promise<void>;
}

export interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info' | 'warning';
  title: string;
  body?: string;
  timeout: number;
  action?: ToastAction;
  /** Set while the action is running, so it cannot be pressed twice. */
  running?: boolean;
}

let nextId = 1;

const EXPANDED_KEY = 'pro-kanban.sidebar.projects';

function readExpandedProjects(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** Transient UI state: toasts, overlays and the global loading indicator. */
export const useUiStore = defineStore('ui', () => {
  const toasts = ref<Toast[]>([]);
  const sidebarCollapsed = ref(localStorage.getItem('pro-kanban.sidebar') === 'collapsed');
  /**
   * The sidebar drawer on narrow viewports. Distinct from `sidebarCollapsed`
   * and deliberately not persisted: a drawer covers the page, so restoring one
   * open on the next visit would hide whatever the user navigated to.
   */
  const mobileNavOpen = ref(false);
  /** Projects whose board list is unfolded in the sidebar, remembered per browser. */
  const expandedProjects = ref<string[]>(readExpandedProjects());
  const commandPaletteOpen = ref(false);
  const searchOpen = ref(false);
  const globalLoading = ref(false);

  function toast(kind: Toast['kind'], title: string, body?: string, timeout = 5000): number {
    const id = nextId++;
    toasts.value = [...toasts.value, { id, kind, title, body, timeout }];
    if (timeout > 0) setTimeout(() => dismiss(id), timeout);
    return id;
  }

  /**
   * A toast with something to press.
   *
   * Given longer than an ordinary one: the point is that somebody who has just
   * realised their mistake still finds it there.
   */
  function action(title: string, body: string | undefined, entry: ToastAction, timeout = 12_000): number {
    const id = nextId++;
    toasts.value = [...toasts.value, { id, kind: 'success', title, body, timeout, action: entry }];
    if (timeout > 0) setTimeout(() => dismiss(id), timeout);
    return id;
  }

  /** Runs a toast's action once, then takes the toast away. */
  async function runAction(id: number): Promise<void> {
    const entry = toasts.value.find((candidate) => candidate.id === id);
    if (!entry?.action || entry.running) return;

    entry.running = true;
    toasts.value = [...toasts.value];
    try {
      await entry.action.run();
    } finally {
      dismiss(id);
    }
  }

  const success = (title: string, body?: string) => toast('success', title, body);
  const error = (title: string, body?: string) => toast('error', title, body, 8000);
  const info = (title: string, body?: string) => toast('info', title, body);
  const warning = (title: string, body?: string) => toast('warning', title, body, 7000);

  function dismiss(id: number): void {
    toasts.value = toasts.value.filter((entry) => entry.id !== id);
  }

  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    localStorage.setItem('pro-kanban.sidebar', sidebarCollapsed.value ? 'collapsed' : 'expanded');
  }

  function openMobileNav(): void {
    mobileNavOpen.value = true;
  }

  function closeMobileNav(): void {
    mobileNavOpen.value = false;
  }

  const isProjectExpanded = (projectId: string): boolean => expandedProjects.value.includes(projectId);

  function setProjectExpanded(projectId: string, expanded: boolean): void {
    if (expanded === isProjectExpanded(projectId)) return;
    expandedProjects.value = expanded
      ? [...expandedProjects.value, projectId]
      : expandedProjects.value.filter((id) => id !== projectId);
    localStorage.setItem(EXPANDED_KEY, JSON.stringify(expandedProjects.value));
  }

  function toggleProject(projectId: string): void {
    setProjectExpanded(projectId, !isProjectExpanded(projectId));
  }

  return {
    toasts,
    action,
    runAction,
    sidebarCollapsed,
    mobileNavOpen,
    openMobileNav,
    closeMobileNav,
    expandedProjects,
    isProjectExpanded,
    setProjectExpanded,
    toggleProject,
    commandPaletteOpen,
    searchOpen,
    globalLoading,
    toast,
    success,
    error,
    info,
    warning,
    dismiss,
    toggleSidebar,
  };
});
